var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Checkbox, FormControlLabel } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { PageContainer, PageContent } from "../../../components/wraper";
import PageHeader from "@ui/components/header/PageHeader";
import { FSelect } from "@ui/components/inputs/FSelect";
import { FInput } from "@ui/components/inputs/FInput";
import { InfoBanner } from "@ui/components/info";
import { alarmNotificationsApi, } from "@api/enpoints/alarm-notifications-api";
import { toast } from "@ui/utils/toaster";
import { useTranslation } from "react-i18next";
export const AcknowledgeAllAlarmsPage = () => {
    const { t } = useTranslation(["common"]);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [causeOptions, setCauseOptions] = useState([{ value: "", label: t("common:actions.selectACause") }]);
    const [showInfoBanner, setShowInfoBanner] = useState(true);
    const { control, handleSubmit, watch, } = useForm({
        defaultValues: {
            cause: "",
            explanation: "",
            alarmHandled: false,
        },
    });
    const watchAlarmHandled = watch("alarmHandled");
    const watchCause = watch("cause");
    useEffect(() => {
        const loadCauses = () => __awaiter(void 0, void 0, void 0, function* () {
            setLoading(true);
            try {
                const causesResponse = yield alarmNotificationsApi.getAlarmCausesDropdown();
                if (causesResponse.isSuccess && causesResponse.data) {
                    setCauseOptions([
                        { value: "", label: t("common:actions.selectACause") },
                        ...causesResponse.data.map((c) => ({
                            value: c.value,
                            label: c.label,
                        })),
                    ]);
                }
            }
            catch (error) {
                console.error("Error loading alarm causes:", error);
                toast.error(t("common:toasts.errorLoadingAlarmData"));
                navigate("/alarms");
            }
            finally {
                setLoading(false);
            }
        });
        void loadCauses();
    }, [navigate, t]);
    const onSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!data.cause) {
            toast.error(t("common:toasts.pleaseSelectCause"));
            return;
        }
        if (!data.alarmHandled) {
            toast.error(t("common:toasts.pleaseCheckAlarmAcknowledged"));
            return;
        }
        setSubmitting(true);
        try {
            const response = yield alarmNotificationsApi.acknowledgeAllAlarms({
                causeId: data.cause,
                explanation: ((_a = data.explanation) === null || _a === void 0 ? void 0 : _a.trim()) || undefined,
            });
            if (response.isSuccess && response.data) {
                const count = (_b = response.data.acknowledgedCount) !== null && _b !== void 0 ? _b : 0;
                const msg = response.data.message ||
                    response.message ||
                    t("common:toasts.alarmsAcknowledgedCount", { count });
                if (count > 0) {
                    toast.success(msg);
                }
                else {
                    toast.info(msg);
                }
                navigate("/alarms");
            }
            else {
                toast.error(response.message || t("common:toasts.failedToAcknowledgeAlarm"));
            }
        }
        catch (error) {
            console.error("Error acknowledging all alarms:", error);
            toast.error(t("common:toasts.errorAcknowledgingAlarms"));
        }
        finally {
            setSubmitting(false);
        }
    });
    const actionButton = {
        label: submitting
            ? t("common:actions.acknowledging")
            : t("common:actions.acknowledgeAll"),
        onClick: handleSubmit(onSubmit),
        variant: "contained",
        size: "large",
        disabled: submitting || !watchAlarmHandled || !watchCause,
    };
    const pageTitle = t("common:pages.acknowledgeAllAlarms");
    if (loading) {
        return (React.createElement(PageContainer, null,
            React.createElement(PageHeader, { title: pageTitle, showBackButton: true }),
            React.createElement(PageContent, null,
                React.createElement(Typography, { color: "text.secondary" }, t("common:status.loading")))));
    }
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: pageTitle, showBackButton: true, bottomActionButton: actionButton }),
        React.createElement(PageContent, { hasBottomAction: true },
            showInfoBanner && (React.createElement(Box, { sx: { mb: 3 } },
                React.createElement(InfoBanner, { title: t("common:actions.importantNotice"), message: t("common:banners.acknowledgeAllAlarms"), onClose: () => setShowInfoBanner(false) }))),
            React.createElement(Typography, { variant: "body1", color: "text.secondary", sx: { mb: 3 } }, t("common:descriptions.acknowledgeAllAlarmsScope")),
            React.createElement(Box, { component: "form", onSubmit: handleSubmit(onSubmit) },
                React.createElement(Grid, { container: true, spacing: 1 },
                    React.createElement(Grid, { size: { xs: 12 } },
                        React.createElement(FSelect, { name: "cause", control: control, label: t("common:labels.cause"), options: causeOptions, required: true, rules: {
                                required: t("common:toasts.pleaseSelectCause"),
                                validate: (value) => (typeof value === "string" && value !== "") ||
                                    t("common:toasts.pleaseSelectCause"),
                            } })),
                    React.createElement(Grid, { size: { xs: 12 } },
                        React.createElement(FInput, { name: "explanation", control: control, label: t("common:labels.explanation"), placeholder: t("common:placeholders.pleaseExplainCause"), multiline: true, rows: 4 })),
                    React.createElement(Grid, { size: { xs: 12 }, pl: 1 },
                        React.createElement(Controller, { name: "alarmHandled", control: control, render: ({ field }) => (React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { checked: field.value, onChange: (e) => field.onChange(e.target.checked) }), label: t("common:labels.alarmAcknowledged"), sx: { mt: 2 } })) })))))));
};
export default AcknowledgeAllAlarmsPage;
